import { Unsubscribe } from 'router5/dist/types/base';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IRouter } from '../../infrastructure/router';
import { RouteParams, Routes } from '../../routes-table';
import { IErrorParsing } from '../../infrastructure/error-parsing';
import { PagingViewModel } from '../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { IOperatorsDAO, IOperatorsDTO } from '../../api/operators.dao';
import { IModuleVM } from '../../infrastructure/components/module-vm';
import TrackingServiceProvider from '../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../../tracking-service/tracking-values';

class OperatorsVM implements IModuleVM {
    @mobx.observable
    public operatorsList: IOperatorsDTO[];
    @mobx.observable
    public errorMessage: string;
    @mobx.observable
    public isLoading: boolean;

    public paginationVM: PagingViewModel;
    private subscriptions: Unsubscribe[];

    @mobx.observable
    public prevPageState: number = 0;

    constructor(
        @IoC.Inject private readonly operatorsDAO: IOperatorsDAO = IoC.Container.get(IOperatorsDAO),
        @IoC.Inject private readonly router: IRouter<RouteParams> = IoC.Container.get(IRouter),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
    ) {
        this.paginationVM = new PagingViewModel();
        mobx.reaction(this.paginationVM.getCurrentPage, this.fetchOperatorsList);
    }

    didMount(): void {
        this.subscriptions = [];

        this.subscriptions.push(this.router.listen(Routes.OperatorsList, async () => {
            this.fetchOperatorsList();
            this.trackEventMixPanel(TRACKING_EVENTS.OPERATOR_TAB_PAGE_LOAD, null);
        }));
    }

    willUnmount(): void {
        this.subscriptions.forEach(x => x());
    }

    @mobx.action
    public fetchOperatorsList = async () => {
        this.isLoading = true;
        try {
            const response = await this.operatorsDAO.getAllOperators({
                page: this.paginationVM.getCurrentPageForBE(),
                size: this.paginationVM.getPageSize(),
            });

            if (this.prevPageState !== this.paginationVM.getCurrentPageForBE()) {
                this.trackEventMixPanel(TRACKING_EVENTS.OPERATOR_TAB_PAGINATION_USED, this.paginationVM.getCurrentPageForBE() + 1);
                this.prevPageState = this.paginationVM.getCurrentPageForBE();
            }
            this.onFetchOperatorsListSuccess(response);
        } catch (error) {
            this.onFetchOperatorsListError(error);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    @mobx.action
    private onFetchOperatorsListSuccess(response): void {
        this.operatorsList = response.content;
        this.paginationVM.setTotalRecords(response.totalElements);
    }

    @mobx.action
    private onFetchOperatorsListError(error): void {
        this.errorMessage = this.errorParsingService.extractMessage(error);
    }

    public trackEventMixPanel = (eventName: string, pageNum: number): void => {
        this.trackingService.get().track(eventName, pageNum && {
            'Page user moved to': pageNum
        });
    };
}

export { OperatorsVM };